import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Fuel, Check, X, Trash2, Receipt, Download, CheckCircle2, AlertTriangle, Clock3, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n) => "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-");
const STATUS_STYLE = {
  in_attesa: { label: "In attesa", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  approvata: { label: "Approvata", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rifiutata: { label: "Rifiutata", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};
const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.in_attesa;
  return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${s.cls}`}>{s.label}</span>;
};
const PcmBadge = ({ r }) => {
  if (r.pcm_synced) return <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700" title={r.pcm_request_id ? `PCM ID: ${r.pcm_request_id}` : "Inviata a PCM"} data-testid={`pcm-ok-${r.id}`}><CheckCircle2 size={12} /> Inviata</span>;
  if (r.pcm_sync_error) return <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700" title={r.pcm_sync_error} data-testid={`pcm-err-${r.id}`}><AlertTriangle size={12} /> Errore sync</span>;
  return <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700" title="Invio a PCM in corso" data-testid={`pcm-pending-${r.id}`}><Clock3 size={12} /> In invio…</span>;
};
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Carburante() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-navy">Carburante</h1>
        <p className="text-sm text-muted-foreground">Richieste di rifornimento da approvare e storico</p>
      </div>
      <Tabs defaultValue="da-approvare">
        <TabsList data-testid="fuel-tabs">
          <TabsTrigger value="da-approvare" data-testid="tab-da-approvare">Da approvare</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico">Storico richieste</TabsTrigger>
        </TabsList>
        <TabsContent value="da-approvare" className="mt-4"><Requests mode="pending" /></TabsContent>
        <TabsContent value="storico" className="mt-4"><Requests mode="history" /></TabsContent>
      </Tabs>
    </div>
  );
}

function Requests({ mode }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(mode === "pending" ? "in_attesa" : "tutte");
  const [reject, setReject] = useState(null);
  const [note, setNote] = useState("");

  const effStatus = mode === "pending" ? "in_attesa" : status;
  const load = () => api.get("/fuel-requests", { params: { status: effStatus } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [effStatus]);
  useLiveRefresh(load);

  const approve = async (id) => {
    try { await api.post(`/fuel-requests/${id}/review`, { action: "approve" }); toast.success("Richiesta approvata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doReject = async () => {
    try { await api.post(`/fuel-requests/${reject.id}/review`, { action: "reject", review_note: note }); toast.success("Richiesta rifiutata"); setReject(null); setNote(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare la richiesta?")) return;
    try { await api.delete(`/fuel-requests/${id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const resync = async (id) => {
    try {
      const { data } = await api.post(`/fuel-requests/${id}/resync`);
      if (data.ok) toast.success("Richiesta inviata a PCM"); else toast.error("Invio a PCM non riuscito");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportFile = (fmt) => {
    const url = `${API}/fuel-requests/export?format=${fmt}${effStatus !== "tutte" ? `&status=${effStatus}` : ""}`;
    const token = localStorage.getItem("flux_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `richieste_carburante.${fmt}`; a.click(); URL.revokeObjectURL(u); })
      .catch(() => toast.error("Export non riuscito"));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {mode === "history" && (
          <div className="w-48">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="fuel-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutte</SelectItem>
                <SelectItem value="approvata">Approvate</SelectItem>
                <SelectItem value="rifiutata">Rifiutate</SelectItem>
                <SelectItem value="in_attesa">In attesa</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => exportFile("xlsx")} data-testid="fuel-export-xlsx"><Download size={16} /> Excel</Button>
          <Button variant="outline" onClick={() => exportFile("csv")} data-testid="fuel-export-csv"><Download size={16} /> CSV</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-14 text-center"><Fuel className="h-9 w-9 text-muted-foreground/40" /><p className="font-semibold">{mode === "pending" ? "Nessuna richiesta da approvare" : "Nessuna richiesta"}</p></Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fuel-requests-table">
              <thead className="bg-secondary/60 text-left text-xs font-bold uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Dipendente</th><th className="px-3 py-2">Carta</th><th className="px-3 py-2">Mezzo</th>
                  <th className="px-3 py-2">Utilizzo</th><th className="px-3 py-2">Carburante</th><th className="px-3 py-2 text-right">Importo</th>
                  <th className="px-3 py-2">Data</th><th className="px-3 py-2">Stato</th><th className="px-3 py-2">PCM</th><th className="px-3 py-2">Scontrino</th><th className="px-3 py-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} data-testid={`fuel-row-${r.id}`} className="border-t border-border/60">
                    <td className="px-3 py-2 font-medium text-navy">{r.employee_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.fuel_card || "-"}</td>
                    <td className="px-3 py-2">{r.vehicle_name}{r.vehicle_plate && <span className="block text-xs text-muted-foreground">{r.vehicle_plate}</span>}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.usage_value} {r.usage_type === "km" ? "km" : "ore"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.fuel_type}</td>
                    <td className="px-3 py-2 text-right"><div className="font-bold text-navy">{fmtEur(r.amount)}</div>{r.actual_amount != null && <div className="text-xs text-muted-foreground">eff. {fmtEur(r.actual_amount)}</div>}{r.liters != null && <div className="text-xs text-muted-foreground">{String(r.liters).replace(".", ",")} L</div>}</td>
                    <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <PcmBadge r={r} />
                        {!r.pcm_synced && <button onClick={() => resync(r.id)} data-testid={`pcm-resync-${r.id}`} title="Riprova invio a PCM" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><RefreshCw size={13} /> Riprova</button>}
                      </div>
                    </td>
                    <td className="px-3 py-2">{r.receipt_url ? <a href={r.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary" data-testid={`fuel-receipt-${r.id}`}><Receipt size={14} /> Vedi</a> : <span className="text-muted-foreground/50">—</span>}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        {r.status === "in_attesa" ? (
                          <>
                            <Button size="sm" onClick={() => approve(r.id)} data-testid={`fuel-approve-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700"><Check size={15} /></Button>
                            <Button size="sm" variant="outline" onClick={() => setReject(r)} data-testid={`fuel-reject-${r.id}`} className="border-rose-200 text-rose-600 hover:bg-rose-50"><X size={15} /></Button>
                          </>
                        ) : <span className="self-center text-xs text-muted-foreground">{r.reviewed_by}</span>}
                        <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`fuel-del-${r.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent data-testid="fuel-reject-dialog">
          <DialogHeader><DialogTitle>Rifiuta richiesta</DialogTitle>
            <DialogDescription>Rifiuti la richiesta di <b>{reject?.employee_name}</b> da {fmtEur(reject?.amount)}?</DialogDescription></DialogHeader>
          <Textarea placeholder="Motivo (facoltativo)" data-testid="fuel-reject-note" value={note} onChange={(e) => setNote(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject(null)}>Annulla</Button>
            <Button onClick={doReject} data-testid="fuel-confirm-reject" className="bg-rose-600 hover:bg-rose-700">Rifiuta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
